import { isOsEmbeddedGuest } from "./oisy";

/**
 * Primary II endpoint — open id.ai directly.
 * AuthClient checks `event.origin` against this URL; routing through
 * identity.internetcomputer.org then redirecting to id.ai breaks that check
 * (switch-identity / passkey UI feels like a crash).
 */
export const DEFAULT_II_IDENTITY_PROVIDER = "https://id.ai";

/** Canonical IC.org II host — redirects to id.ai; keep for CSP / legacy callers. */
export const CANONICAL_II_IDENTITY_PROVIDER = "https://identity.internetcomputer.org";

/** Legacy II endpoint — still valid. */
export const LEGACY_II_IDENTITY_PROVIDER = "https://identity.ic0.app";

/**
 * NFID AuthClient identityProvider — passkeys / inline overlay on desktop.
 * Same AuthClient popup flow as II; does not replace OISY.
 */
export const NFID_IDENTITY_PROVIDER =
  "https://nfid.one/authenticate/?applicationName=Bonsai+OS";

export type IdentityProviderId = "ii" | "nfid";

export const IDENTITY_PROVIDER_URLS: Record<IdentityProviderId, string> = {
  ii: DEFAULT_II_IDENTITY_PROVIDER,
  nfid: NFID_IDENTITY_PROVIDER,
};

/** Sized for id.ai “use a different identity” / passkey UI (not a cramped 525×705 box). */
export const II_POPUP_WINDOW_FEATURES =
  "toolbar=0,location=1,menubar=0,width=600,height=800,left=80,top=40";

/** Guest iframe (`?embed=os`) must delegate II to the Bonsai OS host origin. */
export function shouldDelegateIiToOsHost(search?: string): boolean {
  return isOsEmbeddedGuest(search);
}

export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isMobileBrowser(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const ua = navigator.userAgent || "";
    const mobileUa =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
    const touch =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    return mobileUa || (touch && narrow);
  } catch {
    return false;
  }
}

/**
 * Smart TV / living-room browsers — popups (`window.open`) are usually blocked.
 * Keep detection UA-based; large desktop Chrome must still use II popup.
 */
export function isLivingRoomBrowser(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const ua = navigator.userAgent || "";
    return /Tizen|Web0S|WebOS|Smart[- ]?TV|SMART-TV|BRAVIA|Viera|NetCast|HbbTV|AppleTV|Android TV|GoogleTV|CrKey|AFT[A-Z]|FireTV|Roku|Xbox|PLAYSTATION|Nintendo/i.test(
      ua,
    );
  } catch {
    return false;
  }
}

/** True when AuthClient / window.open likely failed due to a popup blocker. */
export function isPopupAuthFailure(message: string | undefined | null): boolean {
  if (!message) return false;
  return (
    /popup/i.test(message) ||
    /blocked/i.test(message) ||
    /UserInterrupt/i.test(message) ||
    message === "POPUP_BLOCKED"
  );
}

/**
 * II UI “Unable to connect” / passkey bridge failures — often iframe, in-app
 * browsers, or Electron. Prefer full-page / top-level auth retry.
 */
export function isIiConnectFailure(message: string | undefined | null): boolean {
  if (!message) return false;
  if (isPopupAuthFailure(message)) return true;
  return (
    /Unable to connect/i.test(message) ||
    /issue connecting with the application/i.test(message) ||
    /Try a different browser/i.test(message) ||
    /WebAuthn/i.test(message) ||
    /NotAllowedError/i.test(message)
  );
}

/**
 * Full-page /auth/ii redirect — PWA, mobile, living-room TV, or nested hub iframe.
 * Top-level desktop browsers keep II popup so hub window state survives.
 * Same `identityProvider` + `derivationOrigin` as popup → same principal.
 *
 * Important: `/auth/ii` must require a **tap** before `AuthClient.login()` so the
 * II / id.ai window opens under a user gesture (never auto-login from useEffect).
 */
export function preferIiAuthRedirect(opts?: {
  nestedFrame?: boolean;
}): boolean {
  if (typeof window === "undefined") return false;
  if (isStandalonePwa()) return true;
  if (opts?.nestedFrame) return true;
  if (isLivingRoomBrowser()) return true;
  return isMobileBrowser();
}

/** @deprecated Use DEFAULT_II_IDENTITY_PROVIDER — id.ai is the primary host. */
export const II_ID_AI_ORIGIN = "https://id.ai";
export const II_ALLOWED_ORIGINS = [
  DEFAULT_II_IDENTITY_PROVIDER,
  CANONICAL_II_IDENTITY_PROVIDER,
  LEGACY_II_IDENTITY_PROVIDER,
  II_ID_AI_ORIGIN,
] as const;
